import Dexie, { type Table } from "dexie";

import {
  ApiError,
  deleteSet,
  discardSession,
  finishSession,
  logSet,
  type PlanItem,
  type SessionDetail,
  type SetKind,
} from "./api";

/**
 * Local-first: тренировка живёт в IndexedDB, сервер догоняет.
 *
 * Ограничение платформы принято: холодного старта без сети не будет (§2.4 ТЗ).
 * Задача здесь другая — потеря связи посреди тренировки не должна ломать
 * ничего. Поэтому подход сначала ложится в базу на устройстве и в очередь, а
 * уже потом уходит на сервер; каждая мутация несёт клиентский UUID, и сервер
 * по нему идемпотентен, так что ретраи не плодят дублей.
 */

export interface QueuedMutation {
  id?: number;
  sessionId: string;
  kind: "set" | "delete-set" | "finish" | "discard";
  payload: Record<string, unknown>;
  createdAt: number;
}

export interface RejectedMutation {
  id?: number;
  kind: QueuedMutation["kind"];
  reason: string;
  at: number;
}

interface CachedSession {
  client_id: string;
  detail: SessionDetail;
  savedAt: number;
}

interface CachedSwap {
  /** `<sessionId>:<programExerciseId>` — замена живёт в рамках тренировки. */
  key: string;
  sessionId: string;
  item: PlanItem;
}

class RackDb extends Dexie {
  sessions!: Table<CachedSession, string>;
  queue!: Table<QueuedMutation, number>;
  rejected!: Table<RejectedMutation, number>;
  swaps!: Table<CachedSwap, string>;

  constructor() {
    super("rack");
    this.version(1).stores({
      sessions: "client_id",
      queue: "++id, sessionId",
      rejected: "++id",
      swaps: "key, sessionId",
    });
  }
}

export const db = new RackDb();

export const cacheSession = (detail: SessionDetail) =>
  db.sessions.put({ client_id: detail.client_id, detail, savedAt: Date.now() });

export const cachedSession = async (id: string): Promise<SessionDetail | null> =>
  (await db.sessions.get(id))?.detail ?? null;

export const dropSession = (id: string) =>
  Promise.all([
    db.sessions.delete(id),
    db.swaps.where("sessionId").equals(id).delete(),
  ]);

/** Ключ замены — по строке программы; добавленного — по упражнению. */
const swapKey = (sessionId: string, item: PlanItem) =>
  `${sessionId}:${item.program_exercise_id ?? `x${item.exercise.id}`}`;

export const rememberSwap = (sessionId: string, item: PlanItem) =>
  db.swaps.put({ key: swapKey(sessionId, item), sessionId, item });

export async function swapsFor(sessionId: string): Promise<PlanItem[]> {
  const rows = await db.swaps.where("sessionId").equals(sessionId).toArray();
  return rows.map((r) => r.item);
}

/**
 * Подмешать сохранённые замены в план.
 *
 * Сервер восстанавливает замену из уже записанных подходов, но до первого
 * засчитанного подхода знать о ней неоткуда — эту дыру и закрывает кэш.
 */
export function applySwaps(plan: PlanItem[], swaps: PlanItem[]): PlanItem[] {
  if (swaps.length === 0) return plan;
  const key = (item: PlanItem) => item.program_exercise_id ?? `x${item.exercise.id}`;
  const stored = new Map(swaps.map((s) => [key(s), s]));

  const merged = plan.map((item) => stored.get(key(item)) ?? item);
  // Добавленное на ходу сервер восстановит только после первого подхода —
  // до него оно живёт здесь, иначе исчезнет от любого обновления экрана.
  const present = new Set(merged.map(key));
  for (const item of swaps) {
    if (!present.has(key(item))) merged.push(item);
  }
  return merged;
}

export const enqueue = (
  sessionId: string,
  kind: QueuedMutation["kind"],
  payload: Record<string, unknown>,
) => db.queue.add({ sessionId, kind, payload, createdAt: Date.now() });

export const pendingCount = () => db.queue.count();

export const rejectedList = () => db.rejected.toArray();

export const clearRejected = () => db.rejected.clear();

/** Сеть недоступна: fetch падает TypeError, до ApiError дело не доходит. */
const isOffline = (e: unknown) => !(e instanceof ApiError);

async function send(item: QueuedMutation): Promise<void> {
  if (item.kind === "set") {
    await logSet(item.sessionId, item.payload as never);
    return;
  }
  if (item.kind === "delete-set") {
    await deleteSet(item.sessionId, item.payload.client_id as string);
    return;
  }
  if (item.kind === "discard") {
    try {
      await discardSession(item.sessionId);
    } catch (e) {
      // Уже удалена (например, пустое завершение дошло раньше) — цель достигнута.
      if (e instanceof ApiError && e.status === 404) return;
      throw e;
    }
    return;
  }
  await finishSession(item.sessionId, item.payload.notes as string | undefined);
}

let running = false;

export interface FlushResult {
  sent: number;
  pending: number;
  rejected: number;
}

/**
 * Отправить накопленное.
 *
 * Строго по порядку: подход, записанный раньше, должен и уйти раньше, иначе
 * правка обгонит создание. Обрыв связи останавливает разбор очереди целиком —
 * следующие мутации всё равно не пройдут. Отказ сервера, наоборот, повторять
 * бессмысленно: мутация уходит в `rejected`, чтобы про неё сказали вслух,
 * а не молча потеряли.
 */
export async function flush(): Promise<FlushResult> {
  if (running) return { sent: 0, pending: await pendingCount(), rejected: 0 };
  running = true;
  let sent = 0;
  let rejected = 0;
  try {
    const items = await db.queue.orderBy("id").toArray();
    for (const item of items) {
      try {
        await send(item);
        await db.queue.delete(item.id!);
        sent += 1;
      } catch (e) {
        if (isOffline(e)) break;
        await db.rejected.add({
          kind: item.kind,
          reason: e instanceof Error ? e.message : String(e),
          at: Date.now(),
        });
        await db.queue.delete(item.id!);
        rejected += 1;
      }
    }
  } finally {
    running = false;
  }
  return { sent, pending: await pendingCount(), rejected };
}

/** Подписка на возвращение сети. Возвращает функцию отписки. */
export function onReconnect(handler: () => void): () => void {
  window.addEventListener("online", handler);
  return () => window.removeEventListener("online", handler);
}

export type { SetKind };
